"use client";

import { useCallback, useState } from "react";
import { BlockBreadcrumbsV2 } from "@/app/_blocks/BlockBreadcrumbsv2";
import clsx from "clsx";
import * as m from "motion/react-m";
import { EventName } from "@mw/shared/analyticsEventNames";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import type { ILink } from "@ui/components/core/Link";
import type { AgentOrbTextureName } from "@ui/components/core/Orb/textures";
import NextImage from "next/image";
import { AgentsOrbTabbedPanel } from "./AgentsOrbTabbedPanel";
import agentHeroBackground from "@/assets/images/backgrounds/agent-hero-background.png";
import { Noise } from "@ui/components/core/Noise/Noise";
import { ProductBadge } from "@ui/components/navigation/PlatformBadge";

export interface HeroAgentClientProps {
  breadcrumbs?: { href?: string; title: string }[];
  title?: string | null;
  description?: string | null;
  primaryLink?: ILink | null;
  secondaryLink?: ILink | null;
  agentId: string;
  callAgentButtonText: string | null;
  getCallButtonText: string | null;
  defaultTab?: "chat" | "voice";
  initialMessages?: { role: "user" | "agent"; message: string }[];
  /** Where chat messages should anchor in chat mode. Defaults to "top". */
  chatAnchor?: "top" | "bottom";
  orbTexture?: AgentOrbTextureName;
  productBadge?: {
    platform: "creative" | "agents" | "api";
    title: string | null;
    subtitle: string | null;
    href?: string;
  };
}

export default function HeroAgentClient({
  breadcrumbs,
  title,
  description,
  primaryLink,
  secondaryLink,
  agentId,
  callAgentButtonText,
  getCallButtonText,
  defaultTab = "voice",
  initialMessages,
  chatAnchor = "top",
  orbTexture = "agents-1",
  productBadge,
}: HeroAgentClientProps) {
  const [tabbedMode, setTabbedMode] = useState<"chat" | "voice">(defaultTab);
  const handleTabChange = useCallback((mode: "chat" | "voice") => setTabbedMode(mode), []);
  const isVoiceTab = tabbedMode === "voice";

  return (
    <div className='tw-space-y-6 lg:tw-grid tw-grid-cols-2 lg:tw-space-y-0'>
      {/* Left side - Content */}
      <div className={clsx("tw-flex tw-flex-1 tw-flex-col tw-gap-5", "tw-py-8 md:tw-py-12", "tw-px-8 md:tw-px-12")}>
        {productBadge && (
          <ProductBadge
            platform={productBadge.platform}
            title={productBadge.title ?? undefined}
            subtitle={productBadge.subtitle ?? undefined}
            href={productBadge.href}
          />
        )}
        <div className='tw-flex tw-flex-col tw-gap-5 tw-mt-auto'>
          {breadcrumbs && <BlockBreadcrumbsV2 links={breadcrumbs} size='small' hidenOnMobileAfter={0} />}
          <h1 className='tw-max-w-3xl tw-type-5xl'>{title}</h1>
          <div className='tw-max-w-lg'>
            <p className='tw-text-light tw-type-base'>{description}</p>
            {(primaryLink || secondaryLink) && (
              <>
                <div className='tw-flex tw-flex-wrap tw-gap-3 tw-pt-10'>
                  {primaryLink && (
                    <LinkTracked
                      {...primaryLink}
                      asButton
                      buttonVariants={{ variant: "primary", size: "lg" }}
                      eventName={EventName.mw_hero_primary_link}
                    />
                  )}
                  {secondaryLink && (
                    <LinkTracked
                      {...secondaryLink}
                      asButton
                      buttonVariants={{ variant: "elevated", size: "lg" }}
                      eventName={EventName.mw_hero_secondary_link}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Agent Tabbed Panel */}
      <div className='tw-relative tw-flex tw-flex-1 tw-items-center tw-justify-center tw-overflow-hidden tw-px-6 tw-py-8 tw-min-h-[550px] md:tw-min-h-[700px]'>
        <m.div
          className='tw-absolute tw-inset-0'
          initial={false}
          animate={{ opacity: isVoiceTab ? 0 : 1 }}
          transition={{ duration: 0.4 }}
        >
          <NextImage src={agentHeroBackground} alt='' fill className='tw-object-cover' priority placeholder='blur' />
          <Noise />
        </m.div>
        <m.div
          className='tw-absolute tw-inset-0 tw-bg-cream'
          initial={false}
          animate={{ opacity: isVoiceTab ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />
        <AgentsOrbTabbedPanel
          className='tw-relative tw-z-10'
          agentId={agentId}
          inactiveTextColor={isVoiceTab ? "default" : "white"}
          switcherSurface={isVoiceTab ? "cream" : "dark"}
          initialMessages={initialMessages}
          callAgentButtonText={callAgentButtonText}
          getCallButtonText={getCallButtonText}
          defaultTab={defaultTab}
          onTabChange={handleTabChange}
          chatAnchor={chatAnchor}
          orbTexture={orbTexture ?? "agents-1"}
        />
      </div>
    </div>
  );
}
